import { defineStore } from 'pinia';
import {
  get, set, isEmpty, filter, throttle, toLower, first,
  reduce, forEach, cloneDeep,
} from 'lodash-es';
import { stripTags } from '@/lib/utils';
import { getTeiLang } from '@/lib/constants/teiLanguageMap';
import {
  TRY_TYPE_SAVE,
  TRY_TYPE_SUBMIT,
  TRY_TYPE_SCORE_OVERWRITTEN,
  TEI_SAVE_STATE_SAVING,
  TEI_SAVE_STATE_SAVED,
  TEI_SAVE_STATE_ERROR,

} from '@/lib/constants';
import {
  useAppStore,
  useAssetStore,
  useEditorStore,
  useErrorStore,
  useToastStore,
  useTrackingStore,
  useUserStore,
} from '@/stores';
import { parseAssessmentAttempt } from '@/lib/utils/store';
import * as types from '@/lib/constants/store';
import Deferred from '@/lib/deferred';

// Throttle PATCH requests by holding them here and submitting them in batches
let patchList = [];

// Save response state
let saveQueue = [];

const useAssessmentStore = defineStore('studio-assessment', {
  state: () => ({
    assessment: null,
    attempt: null,
    allowEditAttempt: true,
    teis: {}, // cached TEI data
    attemptIsClearing: false,
    updatedTEIs: [], // temporary store for TEIS that have been updated, but not submitted
    isDrawingMode: false, // Track when drawing is active to disable navigation
    saveStateByTeiId: {},
    sendingAttemptPatch: false, // Track when an attempt patch is in flight
  }),
  getters: {
    saveQueueIsSaving() {
      return () => (saveQueue || []).some((item) => item.isSaving);
    },
    pageIsFullSlideTEI: (state) => {
      const editorStore = useEditorStore();
      return !!(
        state.teis
        && editorStore.currentPage?.options
        && editorStore.currentPage.options.asset_id
        && state.teis[editorStore.currentPage.options?.asset_id]
      );
    },
    attemptedTEIs: (state) => {
      let teis = [];
      if (state.attempt?.items) {
        teis = state.attempt.items
          .filter((item) => (!!item.response || item.tries?.length))
          .map((item) => item.id);
      }

      // merge updated
      return Array.from(new Set([...teis, ...state.updatedTEIs]));
    },
    attemptHasUnansweredQuestions() {
      const hasUnansweredQuizItems = this.quizAttemptItems.some((item) => {
        const { response } = item.try;

        // An empty prompt response contains only empty tags
        const promptResponse = get(response, 'answerFieldData[0]');
        const isEmptyPromptResponse = promptResponse !== undefined && !stripTags(promptResponse);

        return !response // the default unanswered value is an empty string
          || isEmptyPromptResponse
          || isEmpty(response); // an unanswered multiple_selection is an empty array
      });
      const hasUnansweredPageTeis = this.pageTeiAttemptItems.some(
        (item) => item.try.type_id !== TRY_TYPE_SUBMIT,
      );

      return hasUnansweredQuizItems || hasUnansweredPageTeis;
    },
    attemptIsSubmitted() {
      // It's considered submitted if any items have type_id === 2 (TRY_TYPE_SUBMIT)
      const quizItemsSubmitted = this.quizAttemptItems
        .some((item) => item.try.type_id === TRY_TYPE_SUBMIT);
      const pageTeisSubmitted = this.pageTeiAttemptItems
        .every((item) => item.try.type_id === TRY_TYPE_SUBMIT);
      const hasQuizItems = !!this.quizAttemptItems.length;
      const hasPageTeis = !!this.pageTeiAttemptItems.length;
      if (hasQuizItems && hasPageTeis) {
        return quizItemsSubmitted && pageTeisSubmitted;
      }
      if (hasQuizItems) return quizItemsSubmitted;
      if (hasPageTeis) return pageTeisSubmitted;
      return false;
    },
    attemptItemByTeiId: (state) => (teiId) => (
      get(state.attempt, 'items', []).find((item) => item.tei_id === teiId)
    ),
    attemptItemIndexMap() {
      return reduce(this.attempt?.items, (map, item, index) => {
        const updateMap = map;

        updateMap[item.tei_id] = index;

        return updateMap;
      }, {});
    },
    teiLang() {
      const editorStore = useEditorStore();
      return getTeiLang(editorStore.draft?.language?.code);
    },
    userCanEditAttempt() {
      return this.userCanEditAttemptQuizItems || this.userCanEditAttemptPageTeis;
    },
    userCanEditAttemptQuizItems() {
      // The user can make PATCHes to their attempt if it hasn't been submitted yet.
      // It's considered submitted if any items have type_id === 2 (TRY_TYPE_SUBMIT)
      // or 9 (TRY_TYPE_SCORE_OVERWRITTEN)
      return this.allowEditAttempt
        && this.attempt
        && !this.quizAttemptItems.some(
          (item) => item.try.type_id === TRY_TYPE_SUBMIT
            || item.try.type_id === TRY_TYPE_SCORE_OVERWRITTEN,
        );
    },
    userCanEditAttemptPageTeis() {
      // Full-slide TEI tries can be submitted multiple times
      return this.allowEditAttempt
        && this.attempt
        && !!this.pageTeiAttemptItems.length;
    },
    pageTeiIds() {
      const editorStore = useEditorStore();
      return editorStore.draft?.tei_page_asset_ids || [];
    },
    hasPageTeis() {
      return !!this.pageTeiIds?.length;
    },
    quizAttemptItems() {
      return filter(this.attempt?.items, (i) => !this.pageTeiIds.includes(i.tei_id));
    },
    pageTeiAttemptItems() {
      return filter(this.attempt?.items, (i) => this.pageTeiIds.includes(i.tei_id));
    },
    /**
     * Check if drawing is allowed for a TEI (from cached TEI data)
     */
    allowDrawing: (state) => (teiId) => state.teis[teiId]?.uiLayout?.allowDrawing || false,
  },
  actions: {
    async [types.CACHE_TEI]({ teiId }) {
      const editorStore = useEditorStore();
      const userStore = useUserStore();
      if (!teiId || this.teis[teiId]) {
        // Return if we've already cached this TEI data
        return;
      }

      try {
        let response;

        if (editorStore.loadAssetsFromContentApi) {
          // DE editors editing lessons use the content api
          response = await this.contentApiFetch(`/api/v1/assets/${teiId}/extended/data`);
        } else if (userStore.userIsStudent) {
          // Students viewing a lesson need TEI data without the answer key
          response = await this.deApiFetch(`/teis/question/${teiId}`);
        } else {
          // Non-students viewing a lesson need the TEI data with the answer key
          response = await this.deApiFetch(`/teis/${teiId}?embed=tei_data`);
        }

        if (response.status !== 200 || !response.data) throw response;

        // These two apis format the response slightly differently
        this.teis[teiId] = {
          ...(response.data.data || response.data.tei_data),
          id: teiId,
        };
      } catch (error) {
        // Display either the error message from Python or the fetch error itself
        this.teis[teiId] = {
          error: get(error, 'response.data.meta', error),
          id: teiId,
        };
      }
    },
    [types.CACHE_TEIS]() {
      const editorStore = useEditorStore();
      if (editorStore.draft.tei_page_asset_ids?.length) {
        editorStore.draft.tei_page_asset_ids.forEach((teiId) => {
          this[types.CACHE_TEI]({ teiId });
        });
      }
    },
    async [types.GET_ASSESSMENT]({ assessmentId }) {
      try {
        const result = await this.deApiFetch(`/assessments/${assessmentId}?embed=questions`);

        if (result.status !== 200) throw result;

        this.assessment = result.data.assessment;
      } catch (error) {
        const errorStore = useErrorStore();
        errorStore[types.SET_ERROR]({
          active: true,
          error: get(error, 'response.data') || {
            meta: {
              message: error,
            },
          },
        });
      }
    },
    async [types.SET_ATTEMPT_CLEARING](value) {
      this.attemptIsClearing = value;
    },
    [types.SET_DRAWING_MODE](value) {
      this.isDrawingMode = value;
    },
    async [types.CLEAR_ATTEMPT]() {
      const appStore = useAppStore();
      const editorStore = useEditorStore();
      const assessmentId = editorStore.draft.references.find(
        (ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'revision',
      ).reference_id;

      const apiUrl = '/assessments/delete_attempts/teacher';
      const bodyFormData = new FormData();
      bodyFormData.append('assessment_id', assessmentId);

      let result;

      this[types.SET_ATTEMPT_CLEARING](true);

      // send request
      try {
        result = await this.deApiFetch(
          apiUrl,
          {
            method: 'POST',
            body: bodyFormData,
            headers: {
              'X-Token': appStore.apiToken,
            },
          },
          false,
        );
        if (result.status !== 200) throw result;

        // success? grab a new attempt
        await this[types.GET_ATTEMPT]({ assessmentId });
      } catch (error) {
        const errorStore = useErrorStore();
        errorStore[types.SET_ERROR]({
          active: true,
          error: get(error, 'response.data') || {
            meta: {
              message: error,
            },
          },
        });
      }

      return result;
    },
    async [types.GET_ATTEMPT](
      {
        assessmentId,
        homeworkId,
        userId,
        ignoreError,
      },
    ) {
      const userStore = useUserStore();
      const appStore = useAppStore();
      const errorStore = useErrorStore();
      const trackingStore = useTrackingStore();

      let attemptUrl = `/assessments/${assessmentId}/attempts?`;
      if (homeworkId) {
        attemptUrl += `&homework_id=${homeworkId}`;

        if (userId) {
          attemptUrl += `&user_guid=${userId}`;
        }
      }
      /*
      NOTE: removed until language sibling attempts are supported
      else {
        attemptUrl += '&include_language_sibling_attempts=1';
      }
      */

      try {
        let newAttemptCreated = false;

        // First, get any existing attempts
        const result = await this.deApiFetch(attemptUrl);
        if (result.status !== 200) throw result;

        // Use the first attempt, filtering by homework id if necessary
        const validAttempt = result.data.attempts.find((attempt) => (
          !homeworkId || toLower(attempt.assignment_id) === toLower(homeworkId)
        ));

        // If no previous valid attempt was returned, we need to create one
        if (!validAttempt) {
          // Teachers that are viewing a student's attempt shouldn't try to create a new attempt
          if (!userStore.userIsStudent && homeworkId) return;

          const newAttempt = await this.deApiFetch(
            attemptUrl,
            {
              method: 'POST',
              body: {}, // no POST body is required
              headers: {
                'X-Token': appStore.apiToken,
              },
            },
          );
          if (newAttempt.status !== 200) throw newAttempt;

          newAttemptCreated = true;

          this[types.SET_ATTEMPT](parseAssessmentAttempt(newAttempt.data.attempt));
        } else {
          this[types.SET_ATTEMPT](parseAssessmentAttempt(validAttempt));
        }

        this[types.CLEAR_SAVE_QUEUE]();

        this[types.INIT_SAVE_STATES]();

        // after an attempt has been set, see if it's submitted
        // OR if the attempt user isn't the same as current user
        if (this.attemptIsSubmitted || (!userStore.userIsStudent && homeworkId)) {
          // disable tracking
          trackingStore[types.DISABLE_ALL_EVENTS]();
        } else if (newAttemptCreated) {
          trackingStore[types.SEND_EVENT]({ eventType: 'attempt-started' });
        }
      } catch (error) {
        // If there's any trouble getting or creating an attempt, show the error
        if (!ignoreError) {
          errorStore[types.SET_ERROR]({
            active: true,
            error: get(error, 'response.data') || {
              meta: {
                message: error,
              },
            },
          });
        }
      }
    },
    [types.SET_ATTEMPT](attempt) {
      const assetStore = useAssetStore();
      // are there assets in the attempt?
      if (attempt && attempt.items) {
        attempt.items.forEach((attemptItem) => {
          const assetId = attemptItem.try?.response?.moduleData?.assetId;

          // cache asset
          if (assetId) {
            assetStore[types.CACHE_ASSET](assetId);
          }
        });
      }

      this.attempt = attempt;
    },
    async [types.SUBMIT_ATTEMPT]() {
      const trackingStore = useTrackingStore();

      // first clear out any patches, as we'll be submitting new ones
      patchList = [];

      /*
        Submit quiz attempt items. Page TEIs submit independently of quiz items.
      */
      this.quizAttemptItems.forEach((attemptItem, attemptItemIdx) => {
        if (attemptItem.try?.response) {
          // To submit an attempt, all tries need to be changed to TRY_TYPE_SUBMIT
          patchList.push({
            op: 'replace',
            path: `/items/${attemptItemIdx}/try/type_id`,
            value: 2, // TRY_TYPE_SUBMIT
          });

          // also submit the response to match up with the new type
          patchList.push({
            op: 'replace',
            path: `/items/${attemptItemIdx}/try/response`,
            value: JSON.stringify(attemptItem.try.response),
          });
        }
      });

      // send tracking store
      trackingStore[types.SEND_EVENT]({ eventType: 'attempt-submitted' });

      await this[types.SEND_ATTEMPT_PATCH]();
    },
    async [types.SEND_ATTEMPT_PATCH]() {
      const appStore = useAppStore();
      const toastStore = useToastStore();
      if (!patchList.length) return;

      const assessmentId = this.attempt.assessment_id;
      const attemptId = this.attempt.id;

      // Stage this request's patches
      const stagedPatchList = patchList;
      patchList = [];
      this.sendingAttemptPatch = true;
      try {
        const response = await this.deApiFetch(
          `/assessments/${assessmentId}/attempts/${attemptId}`,
          {
            method: 'PATCH',
            body: stagedPatchList,
            headers: {
              'X-Token': appStore.apiToken,
            },
          },
        );
        if (response.status !== 200 || !response.data?.attempt) throw response;

        // The attempt that gets returned contains scoring data, so save it
        this.attempt = parseAssessmentAttempt(response.data.attempt);

        if (window.initData && this.attempt.is_scored) {
          const isEmbeddedFormativeSubmitAttempt = window.initData?.forceSingleAttempt
            && window.initData?.formativeGradePassbackEnabled
            && window.initData?.user?.is_student;

          const homework = window.initData?.homework || [];

          if (homework.length > 0 && isEmbeddedFormativeSubmitAttempt) {
            const finalFeedbackResponse = await this.deApiFetch(
              `/assessment/${assessmentId}/attempts/${attemptId}/final_feedback?assignment_id=${homework[0].id}`,
            );

            if (finalFeedbackResponse.status !== 200 || !finalFeedbackResponse.data?.final_feedback) {
              throw finalFeedbackResponse;
            }

            this.attempt.formativeFinalFeedback = (this.attempt.items[0].try.correct)
              ? finalFeedbackResponse.data.final_feedback.correct
              : finalFeedbackResponse.data.final_feedback.incorrect;
          }
        }
      } catch (e) {
        // If the PATCH fails, flash a toast message and add our patches back into the list
        const errorMessage = get(e, 'response.data.meta.message');
        toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMessage });
        patchList = [...stagedPatchList, ...patchList];
      } finally {
        this.sendingAttemptPatch = false;
      }
    },
    [types.THROTTLE_SEND_ATTEMPT_PATCH]: throttle((store) => {
      store[types.SEND_ATTEMPT_PATCH]();
    }, 5000),
    [types.UPDATE_RESPONSE]({ payload, teiId }) {
      const trackingStore = useTrackingStore();
      const attemptItemIdx = get(this, 'attempt.items', [])
        .findIndex((attemptItem) => attemptItem.tei_id === teiId);

      // Add our updated response to the PATCH queue
      patchList.push({
        op: 'replace',
        path: `/items/${attemptItemIdx}/try/response`,
        value: JSON.stringify(payload), // TEI responses are stringified in the DB
      });

      this[types.THROTTLE_SEND_ATTEMPT_PATCH](this);

      // update the store
      set(this, `attempt.items[${attemptItemIdx}].try.response`, payload);

      // send the event
      trackingStore[types.SEND_EVENT]({ eventType: 'tei.update' });
    },
    async [types.SUBMIT_RESPONSE]({ payload, teiId }) {
      const trackingStore = useTrackingStore();
      const attemptItemIdx = get(this, 'attempt.items', [])
        .findIndex((attemptItem) => attemptItem.tei_id === teiId);

      // Wait for pending saves to finish.
      await this[types.WAIT_SAVE_QUEUE_FINISHED]();

      // Clear the save state for this tei after submit.
      this[types.CLEAR_SAVE_STATE](teiId);

      // Add our updated response to the PATCH queue
      patchList.push({
        op: 'replace',
        path: `/items/${attemptItemIdx}/try/response`,
        value: JSON.stringify(payload), // TEI responses are stringified in the DB
      });

      // Update the try to TRY_TYPE_SUBMIT
      patchList.push({
        op: 'replace',
        path: `/items/${attemptItemIdx}/try/type_id`,
        value: TRY_TYPE_SUBMIT,
      });

      // send the event
      trackingStore[types.SEND_EVENT]({ eventType: 'tei.update' });

      this[types.THROTTLE_SEND_ATTEMPT_PATCH](this);
    },
    [types.SET_ALLOW_EDIT_ATTEMPT](allowEdit) {
      this.allowEditAttempt = allowEdit;
    },
    [types.UPDATE_ATTEMPTED_TEI](teiId) {
      const trackingStore = useTrackingStore();

      if (!this.attemptedTEIs.includes(teiId)) {
        this.attemptedTEIs.push(teiId);
      }

      // send the event
      trackingStore[types.SEND_EVENT]({ eventType: 'tei.update' });
    },

    [types.UPDATE_SAVE_STATES](responses, saveState) {
      responses.forEach(({ teiId }) => {
        this.saveStateByTeiId[teiId] = saveState;
      });
    },

    [types.INIT_SAVE_STATES]() {
      forEach(this.attempt?.items, (item) => {
        const itemTry = item.try;
        const response = itemTry?.response;
        /*
          Any value other than an empty string means that we have saved
          user responses for this try. This includes the user clearing response
          which will save a 'null' value or an empty object.
        */
        const hasSavedResponse = response !== ''
          && itemTry?.type_id === TRY_TYPE_SAVE;

        this[types.UPDATE_SAVE_STATES](
          [{ teiId: item.tei_id }],
          hasSavedResponse ? TEI_SAVE_STATE_SAVED : null,
        );
      });
    },

    [types.CLEAR_SAVE_QUEUE]() {
      this.saveStateByTeiId = {};
      saveQueue = [];
    },

    [types.CLEAR_SAVE_STATE](teiId) {
      this[types.UPDATE_SAVE_STATES]([{ teiId }], null);
    },

    async [types.WAIT_SAVE_QUEUE_FINISHED]() {
      return Promise.all(saveQueue.map((item) => item.promise));
    },

    [types.CLEAR_RESPONSE](teiId) {
      this[types.SAVE_RESPONSES]({
        responses: [{ teiId, response: null }],
      });
    },

    /*
      Records one or more TEI responses and enqueues them to be persisted to
      the server. This is the single entry point for both auto-saves (typing in
      a TEI) and the requeue-on-failure path from PROCESS_SAVE_QUEUE.

      - responses:     [{ teiId, response }] to save.
      - prepend:       add these to the front of the pending batch (used when
                       re-queuing failed responses so they retry first).
      - updateAttempt: when true, optimistically mutate local attempt state so
                       the UI reflects the save immediately. The failure-retry
                       path passes false because local state was already updated
                       on the original attempt.
    */
    [types.SAVE_RESPONSES]({
      responses, // [{ teiId, response }]
      prepend = false,
      updateAttempt = true,
    }) {
      if (updateAttempt) {
        // Optimistically update local attempt state before the network call so
        // the UI reflects the new responses without waiting for the server.
        responses.forEach(({ teiId, response }) => {
          const attemptItemIdx = this.attemptItemIndexMap[teiId];

          const currentItem = this.attempt?.items?.[attemptItemIdx];
          const currentTry = currentItem?.try;
          const currentTryTypeId = currentTry?.type_id;

          // If the current try was already submitted, we can't overwrite it.
          // Start a fresh try (cloned with an incremented id) and append it to
          // the item's tries history so the submitted attempt is preserved.
          if (currentTryTypeId === TRY_TYPE_SUBMIT) {
            const newTry = cloneDeep(currentTry);

            newTry.id = currentTry?.id + 1;

            currentItem.tries.push(newTry);
            currentItem.try = newTry;
          }

          // Write the response onto the active try and mark it as a (not yet
          // checked) save rather than a submit.
          set(this, `attempt.items[${attemptItemIdx}].try.response`, response);
          set(this, `attempt.items[${attemptItemIdx}].try.type_id`, TRY_TYPE_SAVE);
          set(this, `attempt.items[${attemptItemIdx}].try.checked`, false);

          if (teiId) {
            this[types.UPDATE_ATTEMPTED_TEI](teiId);
          }
        });

        // Flag these TEIs as "saving" so the UI can show in-progress indicators.
        this[types.UPDATE_SAVE_STATES](
          responses,
          TEI_SAVE_STATE_SAVING,
        );
      }

      // Find an in-flight batch (if any) and the next batch still accepting
      // responses. A new save is always coalesced into a single pending batch
      // so rapid edits get sent together rather than one request per keystroke.
      const isSaving = !!saveQueue.find((i) => i.isSaving);
      let pendingQueueItem = saveQueue.find((i) => !i.isSaving);

      // No batch is waiting to be sent yet, so create one. The deferred lets
      // callers await this batch's completion via its promise.
      if (!pendingQueueItem) {
        const deferred = new Deferred();

        pendingQueueItem = {
          deferred,
          promise: deferred.promise(),
          isSaving: false,
          responses: [],
        };

        saveQueue.push(pendingQueueItem);
      }

      // Merge the responses into the pending batch. Retries prepend so they are
      // re-sent ahead of any newer edits.
      const action = prepend ? 'unshift' : 'push';

      pendingQueueItem.responses[action](...responses);

      // Only kick off processing if nothing is currently saving; an in-flight
      // request will pick up this batch when it finishes (see the finally block
      // in PROCESS_SAVE_QUEUE).
      if (!isSaving) {
        this[types.PROCESS_SAVE_QUEUE]();
      }
    },

    /*
      Sends the oldest batch in the save queue to the server as a JSON-patch
      PATCH, then recursively drains any batches that accumulated while the
      request was in flight. Only one batch is ever in flight at a time, which
      is enforced by SAVE_RESPONSES (it only starts processing when nothing is
      saving) and by the recursive call in the finally block below.
    */
    async [types.PROCESS_SAVE_QUEUE]() {
      const appStore = useAppStore();
      const toastStore = useToastStore();
      const assessmentId = this.attempt.assessment_id;
      const attemptId = this.attempt.id;
      // Always process the head of the queue (oldest batch first).
      const queueItem = first(saveQueue);
      let shouldProcessSaveQueue = true;

      // Nothing queued — nothing to do.
      if (!queueItem) {
        return;
      }

      // Build the JSON-patch payload: each TEI response becomes two replace ops
      // (the serialized response and the save type_id) targeting that item's
      // active try by its index within the attempt.
      const queueItemPatches = queueItem.responses
        .filter((item) => item.teiId)
        .flatMap(({ teiId, response }) => {
          const patches = [];

          const attemptItemIdx = this.attemptItemIndexMap[teiId];

          patches.push({
            op: 'replace',
            path: `/items/${attemptItemIdx}/try/response`,
            value: JSON.stringify(response),
          });

          patches.push({
            op: 'replace',
            path: `/items/${attemptItemIdx}/try/type_id`,
            value: TRY_TYPE_SAVE,
          });

          return patches;
        });

      try {
        // Mark this batch as in flight so SAVE_RESPONSES won't start a second
        // concurrent run and will instead coalesce new edits into a fresh batch.
        queueItem.isSaving = true;

        this[types.UPDATE_SAVE_STATES](
          queueItem.responses,
          TEI_SAVE_STATE_SAVING,
        );

        const response = await this.deApiFetch(
          `/assessments/${assessmentId}/attempts/${attemptId}`,
          {
            method: 'PATCH',
            body: queueItemPatches,
            headers: {
              'X-Token': appStore.apiToken,
            },
          },
        );

        if (response.status !== 200 || !response.data?.attempt) {
          throw response;
        }

        // Only overwrite local attempt state from the server response when this
        // was the last batch. If newer batches are still pending, adopting the
        // server snapshot now would clobber the optimistic local edits they
        // already applied.
        const hasQueuedItems = !!saveQueue.find((i) => !i.isSaving);

        if (!hasQueuedItems) {
          this.attempt = parseAssessmentAttempt(response.data.attempt);
        }

        // Mark these TEIs as successfully saved for the UI.
        this[types.UPDATE_SAVE_STATES](
          queueItem.responses,
          TEI_SAVE_STATE_SAVED,
        );
      } catch (e) {
        // Surface the server error to the user and flag the affected TEIs as
        // errored so the UI can show a failed-save state.
        const errorMessage = get(e, 'response.data.meta.message');

        toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMessage });

        this[types.UPDATE_SAVE_STATES](
          queueItem.responses,
          TEI_SAVE_STATE_ERROR,
        );

        /*
          On error, only process the save queue again if there is a pending
          queue item that is not currently saving.
          Otherwise, we'll add the responses that errored back to the queue
          and they will be processed the next time a response is saved
          or submitted.
        */
        shouldProcessSaveQueue = !!saveQueue.find((i) => !i.isSaving);

        // Re-queue the failed responses at the front of the next batch so they
        // retry first. updateAttempt is false because local state was already
        // updated when these responses were first saved.
        this[types.SAVE_RESPONSES]({
          responses: queueItem.responses,
          prepend: true,
          updateAttempt: false,
        });
      } finally {
        // Resolve the batch's promise (waking any awaiters), drop it from the
        // head of the queue, and continue draining if another batch is ready.
        queueItem.deferred.resolve();

        saveQueue.shift();

        if (shouldProcessSaveQueue) {
          this[types.PROCESS_SAVE_QUEUE]();
        }
      }
    },
  },
});

export default useAssessmentStore;
